/**
 * Groendak Configurator - Modern & Modular JavaScript
 *
 * This configurator handles:
 * - Step navigation
 * - Dimension calculations
 * - Product selection
 * - Price calculations
 * - Summary generation
 * - Cart integration
 */

class GroendakConfigurator {
  constructor(container) {
    this.container = container;
    this.currentStep = 0;
    this.totalSteps = 0; // Will be set in cacheElements

    this.state = {
      dimensions: {
        width: 0,
        length: 0,
        area: 0,
        perimeter: 0,
        inputMethod: 'dimensions' // 'dimensions' or 'area'
      },
      selections: new Map(),
      products: new Map(),
      calculationTypes: new Map()
    };

    this.init();
  }

  /**
   * Initialize configurator
   */
  init() {
    this.cacheElements();
    this.bindEvents();
    this.loadFromStorage();
    this.updateUI();
  }

  /**
   * Cache DOM elements
   */
  cacheElements() {
    this.elements = {
      steps: this.container.querySelectorAll('.step'),
      navItems: this.container.querySelectorAll('.step-nav-item'),
    };

    // Set total steps after caching elements
    this.totalSteps = this.elements.steps.length;

    // Continue caching
    Object.assign(this.elements, {

      // Input method tabs
      inputMethodTabs: this.container.querySelectorAll('.input-method-tab'),
      inputMethodPanels: this.container.querySelectorAll('.input-method-panel'),

      // Dimension inputs
      widthInput: this.container.querySelector('#roof-width'),
      lengthInput: this.container.querySelector('#roof-length'),
      areaInput: this.container.querySelector('#roof-area'),
      perimeterInput: this.container.querySelector('#roof-perimeter'),

      // Calculated displays
      calculatedArea: this.container.querySelector('#calculated-m2'),
      calculatedPerimeter: this.container.querySelector('#calculated-perimeter'),

      // Sidebar
      sidebarArea: this.container.querySelector('#sidebar-area'),
      sidebarPerimeter: this.container.querySelector('#sidebar-perimeter'),
      sidebarTotal: this.container.querySelector('#sidebar-total'),
      sidebarSelectionsList: this.container.querySelector('#sidebar-selections-list'),
      roofWidthLabel: this.container.querySelector('#roof-width-label'),
      roofLengthLabel: this.container.querySelector('#roof-length-label'),

      // Summary
      summaryArea: this.container.querySelector('#summary-area'),
      summaryPerimeter: this.container.querySelector('#summary-perimeter'),
      summaryProducts: this.container.querySelector('#summary-products'),
      summarySubtotal: this.container.querySelector('#summary-subtotal'),
      summaryVat: this.container.querySelector('#summary-vat'),
      summaryGrandTotal: this.container.querySelector('#summary-grand-total'),

      // Buttons
      nextButtons: this.container.querySelectorAll('[data-next-step]'),
      prevButtons: this.container.querySelectorAll('[data-prev-step]'),
      addToCartButton: this.container.querySelector('#add-to-cart-btn')
    });
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Input method tabs
    if (this.elements.inputMethodTabs) {
      this.elements.inputMethodTabs.forEach(tab => {
        tab.addEventListener('click', () => this.switchInputMethod(tab.dataset.method));
      });
    }

    // Dimension inputs
    if (this.elements.widthInput) {
      this.elements.widthInput.addEventListener('input', () => this.handleDimensionInput());
    }
    if (this.elements.lengthInput) {
      this.elements.lengthInput.addEventListener('input', () => this.handleDimensionInput());
    }
    if (this.elements.areaInput) {
      this.elements.areaInput.addEventListener('input', () => this.handleAreaInput());
    }
    if (this.elements.perimeterInput) {
      this.elements.perimeterInput.addEventListener('input', () => this.handlePerimeterInput());
    }

    // Product selection
    this.container.querySelectorAll('input[type="radio"][name^="step-"]').forEach(radio => {
      radio.addEventListener('change', (e) => this.handleProductSelection(e));
    });

    // Onderhoud: meststof checkbox
    this.container.querySelectorAll('input[data-meststof]').forEach(checkbox => {
      checkbox.addEventListener('change', (e) => this.handleMeststofToggle(e));
    });

    // Product info modals
    this.container.querySelectorAll('[data-product-info]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.showProductModal(btn);
      });
    });

    this.container.querySelectorAll('[data-modal-close]').forEach(btn => {
      btn.addEventListener('click', () => this.closeProductModal(btn));
    });

    this.container.querySelectorAll('.product-modal-overlay').forEach(overlay => {
      overlay.addEventListener('click', () => this.closeProductModal(overlay));
    });

    // Navigation
    if (this.elements.nextButtons) {
      this.elements.nextButtons.forEach(btn => {
        btn.addEventListener('click', () => this.nextStep());
      });
    }

    if (this.elements.prevButtons) {
      this.elements.prevButtons.forEach(btn => {
        btn.addEventListener('click', () => this.prevStep());
      });
    }

    // Step navigation items (clickable)
    if (this.elements.navItems) {
      this.elements.navItems.forEach((item, index) => {
        item.addEventListener('click', () => {
          if (index <= this.currentStep || this.canNavigateToStep(index)) {
            this.goToStep(index);
          }
        });
      });
    }

    // Add to cart
    if (this.elements.addToCartButton) {
      this.elements.addToCartButton.addEventListener('click', () => this.addToCart());
    }

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAllModals();
      }
    });
  }

  /**
   * Switch input method (dimensions vs direct area)
   */
  switchInputMethod(method) {
    this.state.dimensions.inputMethod = method;

    // Update tabs
    this.elements.inputMethodTabs.forEach(tab => {
      tab.classList.toggle('active', tab.dataset.method === method);
    });

    // Update panels
    this.elements.inputMethodPanels.forEach(panel => {
      panel.classList.toggle('active', panel.dataset.methodPanel === method);
    });

    this.updateCalculations();
    this.saveToStorage();
  }

  /**
   * Handle dimension input (width & length)
   */
  handleDimensionInput() {
    const width = parseFloat(this.elements.widthInput.value) || 0;
    const length = parseFloat(this.elements.lengthInput.value) || 0;

    this.state.dimensions.width = width;
    this.state.dimensions.length = length;

    if (this.state.dimensions.inputMethod === 'dimensions') {
      this.state.dimensions.area = width * length;

      // Auto-calculate perimeter if not manually entered
      if (!this.elements.perimeterInput.value) {
        this.state.dimensions.perimeter = 2 * (width + length);
      }
    }

    this.updateCalculations();
    this.saveToStorage();
  }

  /**
   * Handle direct area input
   */
  handleAreaInput() {
    const area = parseFloat(this.elements.areaInput.value) || 0;
    this.state.dimensions.area = area;

    this.updateCalculations();
    this.saveToStorage();
  }

  /**
   * Handle perimeter input
   */
  handlePerimeterInput() {
    const perimeter = parseFloat(this.elements.perimeterInput.value) || 0;
    this.state.dimensions.perimeter = perimeter;

    this.updateCalculations();
    this.saveToStorage();
  }

  /**
   * Update all calculations and displays
   */
  updateCalculations() {
    const { width, length, area, perimeter, inputMethod } = this.state.dimensions;

    // Update calculated area display
    if (this.elements.calculatedArea && inputMethod === 'dimensions') {
      this.elements.calculatedArea.textContent = area.toFixed(2) + ' m²';
    }

    // Update calculated/auto perimeter
    if (this.elements.calculatedPerimeter) {
      if (inputMethod === 'dimensions' && !this.elements.perimeterInput.value) {
        this.elements.calculatedPerimeter.textContent = perimeter.toFixed(2) + ' m';
      } else if (this.elements.perimeterInput.value) {
        this.elements.calculatedPerimeter.textContent = perimeter.toFixed(2) + ' m';
      }
    }

    // Update sidebar
    if (this.elements.sidebarArea) {
      this.elements.sidebarArea.textContent = area.toFixed(2) + ' m²';
    }
    if (this.elements.sidebarPerimeter) {
      this.elements.sidebarPerimeter.textContent = perimeter.toFixed(2) + ' m';
    }

    // Update roof diagram labels
    if (this.elements.roofWidthLabel && width > 0) {
      this.elements.roofWidthLabel.textContent = width.toFixed(2) + ' m';
    }
    if (this.elements.roofLengthLabel && length > 0) {
      this.elements.roofLengthLabel.textContent = length.toFixed(2) + ' m';
    }

    // Recalculate total price
    this.calculateTotal();
  }

  /**
   * Handle product selection
   */
  handleProductSelection(event) {
    const radio = event.target;
    const stepIndex = this.getStepIndexFromRadio(radio);
    const productGrid = radio.closest('.product-grid');
    const calculationType = productGrid?.dataset.calculationType || 'unit';

    if (radio.dataset.skip) {
      // Skip this step - remove from selections
      this.state.selections.delete(stepIndex);
      this.state.calculationTypes.delete(stepIndex);
    } else {
      // Store selection
      const productData = {
        variantId: radio.value,
        title: radio.dataset.productTitle,
        price: parseFloat(radio.dataset.productPrice),
        image: radio.dataset.productImage
      };

      this.state.selections.set(stepIndex, productData);
      this.state.calculationTypes.set(stepIndex, calculationType);
    }

    // Update totals and sidebar
    this.calculateTotal();
    this.saveToStorage();
  }

  /**
   * Handle meststof checkbox toggle
   */
  handleMeststofToggle(event) {
    const checkbox = event.target;
    const stepContainer = checkbox.closest('.onderhoud-upsell');
    const stepIndex = parseInt(stepContainer.dataset.stepIndex);
    const selectionKey = `meststof-${stepIndex}`;

    if (checkbox.checked) {
      const coverage = parseInt(checkbox.dataset.coverage) || 20;
      const quantity = Math.max(1, Math.ceil(this.state.dimensions.area / coverage));

      const productData = {
        variantId: checkbox.value,
        title: checkbox.dataset.productTitle,
        price: parseFloat(checkbox.dataset.productPrice),
        image: checkbox.dataset.productImage,
        calculatedQuantity: quantity,
        isMeststof: true,
        coverage: coverage
      };

      this.state.selections.set(selectionKey, productData);
      this.state.calculationTypes.set(selectionKey, 'meststof');
    } else {
      this.state.selections.delete(selectionKey);
      this.state.calculationTypes.delete(selectionKey);
    }

    this.calculateTotal();
    this.saveToStorage();
  }

  /**
   * Get step index from radio name
   */
  getStepIndexFromRadio(radio) {
    const match = radio.name.match(/step-(\d+)-product/);
    return match ? parseInt(match[1]) : 0;
  }

  /**
   * Calculate total price
   */
  calculateTotal() {
    let subtotal = 0;

    this.state.selections.forEach((product, stepIndex) => {
      const calculationType = this.state.calculationTypes.get(stepIndex);
      let quantity = 1;

      switch (calculationType) {
        case 'area':
          quantity = Math.ceil(this.state.dimensions.area);
          break;
        case 'perimeter':
          quantity = Math.ceil(this.state.dimensions.perimeter);
          break;
        case 'meststof':
          quantity = Math.max(1, Math.ceil(this.state.dimensions.area / (product.coverage || 20)));
          break;
        case 'unit':
        default:
          quantity = 1;
          break;
      }

      const lineTotal = product.price * quantity;
      subtotal += lineTotal;

      // Store calculated quantity for cart
      product.calculatedQuantity = quantity;
    });

    // Update sidebar selections list
    this.updateSidebarSelections();

    const vat = subtotal * 0.21;
    const grandTotal = subtotal + vat;

    // Update sidebar
    if (this.elements.sidebarTotal) {
      this.elements.sidebarTotal.textContent = this.formatPrice(grandTotal);
    }

    // Update summary
    if (this.elements.summarySubtotal) {
      this.elements.summarySubtotal.textContent = this.formatPrice(subtotal);
    }
    if (this.elements.summaryVat) {
      this.elements.summaryVat.textContent = this.formatPrice(vat);
    }
    if (this.elements.summaryGrandTotal) {
      this.elements.summaryGrandTotal.textContent = this.formatPrice(grandTotal);
    }

    return { subtotal, vat, grandTotal };
  }

  /**
   * Format price for display
   */
  formatPrice(amount) {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  }

  /**
   * Update sidebar selections list
   */
  updateSidebarSelections() {
    if (!this.elements.sidebarSelectionsList) return;

    // Clear current list
    this.elements.sidebarSelectionsList.innerHTML = '';

    // Check if there are any selections
    if (this.state.selections.size === 0) {
      this.elements.sidebarSelectionsList.innerHTML = '<p class="sidebar-selections-empty">Nog geen producten geselecteerd</p>';
      return;
    }

    // Add each selection
    this.state.selections.forEach((product, stepIndex) => {
      const calculationType = this.state.calculationTypes.get(stepIndex);
      const quantity = product.calculatedQuantity || 1;

      let detailsText = '';
      switch (calculationType) {
        case 'area':
          detailsText = `${quantity} m² × ${this.formatPrice(product.price)}/m²`;
          break;
        case 'perimeter':
          detailsText = `${quantity} m × ${this.formatPrice(product.price)}/m`;
          break;
        case 'meststof':
          detailsText = `${quantity} emmer(s) (1 per ${product.coverage || 20} m²)`;
          break;
        case 'unit':
        default:
          if (quantity > 1) {
            detailsText = `${quantity}× ${this.formatPrice(product.price)}`;
          } else {
            detailsText = this.formatPrice(product.price);
          }
          break;
      }

      const itemHTML = `
        <div class="sidebar-selection-item">
          <svg class="sidebar-selection-item-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          <div class="sidebar-selection-item-content">
            <div class="sidebar-selection-item-title">${product.title}</div>
            <div class="sidebar-selection-item-details">${detailsText}</div>
          </div>
        </div>
      `;

      this.elements.sidebarSelectionsList.insertAdjacentHTML('beforeend', itemHTML);
    });
  }

  /**
   * Check if can navigate to step (validation)
   */
  canNavigateToStep(targetStep) {
    // Step 0: Check dimensions
    if (targetStep > 0 && this.state.dimensions.area <= 0) {
      this.showError('Voer eerst de afmetingen van uw dak in');
      return false;
    }

    // Check previous required steps (only steps with a product-grid)
    for (let i = 1; i < targetStep; i++) {
      const step = this.elements.steps[i];
      const productGrid = step?.querySelector('.product-grid');

      // Skip steps without a product grid (e.g. onderhoud_step)
      if (!productGrid) continue;

      const isRequired = productGrid.dataset.required !== 'false';

      if (isRequired && !this.state.selections.has(i)) {
        this.showError('Maak eerst een keuze in de vorige stappen');
        return false;
      }
    }

    return true;
  }

  /**
   * Navigate to next step
   */
  nextStep() {
    if (this.currentStep < this.totalSteps - 1) {
      const nextStep = this.currentStep + 1;

      if (this.canNavigateToStep(nextStep)) {
        this.goToStep(nextStep);
      }
    }
  }

  /**
   * Navigate to previous step
   */
  prevStep() {
    if (this.currentStep > 0) {
      this.goToStep(this.currentStep - 1);
    }
  }

  /**
   * Go to specific step
   */
  goToStep(stepIndex) {
    this.currentStep = stepIndex;

    // Update steps visibility
    this.elements.steps.forEach((step, index) => {
      step.classList.toggle('step--active', index === stepIndex);
    });

    // Update navigation
    this.elements.navItems.forEach((item, index) => {
      item.classList.remove('active', 'completed');

      if (index === stepIndex) {
        item.classList.add('active');
      } else if (index < stepIndex) {
        item.classList.add('completed');
      }
    });

    // If on summary step, generate summary
    if (stepIndex === this.totalSteps - 1) {
      this.generateSummary();
    }

    // Scroll to top
    this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });

    this.saveToStorage();
  }

  /**
   * Generate summary
   */
  generateSummary() {
    // Update dimensions
    if (this.elements.summaryArea) {
      this.elements.summaryArea.textContent = this.state.dimensions.area.toFixed(2) + ' m²';
    }
    if (this.elements.summaryPerimeter) {
      this.elements.summaryPerimeter.textContent = this.state.dimensions.perimeter.toFixed(2) + ' m';
    }

    // Generate product list
    if (this.elements.summaryProducts) {
      this.elements.summaryProducts.innerHTML = '';

      this.state.selections.forEach((product, stepIndex) => {
        const calculationType = this.state.calculationTypes.get(stepIndex);
        const quantity = product.calculatedQuantity || 1;
        const lineTotal = product.price * quantity;

        let calcLabel = '';
        switch (calculationType) {
          case 'area':
            calcLabel = `${quantity} m² × ${this.formatPrice(product.price)}/m²`;
            break;
          case 'perimeter':
            calcLabel = `${quantity} m × ${this.formatPrice(product.price)}/m`;
            break;
          case 'meststof':
            calcLabel = `${quantity} emmer(s) × ${this.formatPrice(product.price)}`;
            break;
          case 'unit':
          default:
            calcLabel = `${quantity} stuks × ${this.formatPrice(product.price)}`;
            break;
        }

        const productHTML = `
          <div class="summary-product-item">
            <div class="summary-product-image">
              ${product.image ? `<img src="${product.image}" alt="${product.title}">` : ''}
            </div>
            <div class="summary-product-details">
              <h4>${product.title}</h4>
              <p>${calcLabel}</p>
            </div>
            <div class="summary-product-price">
              <span class="total-price">${this.formatPrice(lineTotal)}</span>
            </div>
          </div>
        `;

        this.elements.summaryProducts.insertAdjacentHTML('beforeend', productHTML);
      });
    }

    // Recalculate totals
    this.calculateTotal();
  }

  /**
   * Show product modal
   */
  showProductModal(button) {
    const productCard = button.closest('.product-card');
    const modal = productCard?.querySelector('.product-modal');

    if (modal) {
      modal.classList.add('active');
      document.body.style.overflow = 'hidden';
    }
  }

  /**
   * Close product modal
   */
  closeProductModal(element) {
    const modal = element.closest('.product-modal');

    if (modal) {
      modal.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  /**
   * Close all modals
   */
  closeAllModals() {
    this.container.querySelectorAll('.product-modal.active').forEach(modal => {
      modal.classList.remove('active');
    });
    document.body.style.overflow = '';
  }

  /**
   * Add to cart
   */
  async addToCart() {
    if (this.state.selections.size === 0) {
      this.showError('Selecteer minimaal één product');
      return;
    }

    // Prepare cart items
    const items = [];
    this.state.selections.forEach((product) => {
      items.push({
        id: parseInt(product.variantId),
        quantity: product.calculatedQuantity || 1
      });
    });

    // Show loading state
    this.elements.addToCartButton.classList.add('btn--loading');
    this.elements.addToCartButton.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        throw new Error('Toevoegen aan winkelwagen mislukt');
      }

      const data = await response.json();

      // Success!
      this.showSuccess('Producten succesvol toegevoegd aan winkelwagen');

      // Clear storage and reload after delay
      setTimeout(() => {
        this.clearStorage();
        window.location.href = '/cart';
      }, 1500);

    } catch (error) {
      console.error('Cart error:', error);
      this.showError('Er ging iets mis. Probeer het opnieuw.');

      // Reset button
      this.elements.addToCartButton.classList.remove('btn--loading');
      this.elements.addToCartButton.disabled = false;
    }
  }

  /**
   * Show error toast
   */
  showError(message) {
    if (window.showToast) {
      window.showToast(message, 'error');
    } else {
      alert(message);
    }
  }

  /**
   * Show success toast
   */
  showSuccess(message) {
    if (window.showToast) {
      window.showToast(message, 'success');
    } else {
      alert(message);
    }
  }

  /**
   * Save state to localStorage
   */
  saveToStorage() {
    const storageData = {
      currentStep: this.currentStep,
      dimensions: this.state.dimensions,
      selections: Array.from(this.state.selections.entries()),
      calculationTypes: Array.from(this.state.calculationTypes.entries())
    };

    try {
      localStorage.setItem('groendak-configurator', JSON.stringify(storageData));
    } catch (e) {
      console.warn('Could not save to localStorage', e);
    }
  }

  /**
   * Load state from localStorage
   */
  loadFromStorage() {
    try {
      const stored = localStorage.getItem('groendak-configurator');

      if (stored) {
        const data = JSON.parse(stored);

        // Restore dimensions
        if (data.dimensions) {
          this.state.dimensions = data.dimensions;

          if (this.elements.widthInput) this.elements.widthInput.value = data.dimensions.width || '';
          if (this.elements.lengthInput) this.elements.lengthInput.value = data.dimensions.length || '';
          if (this.elements.areaInput) this.elements.areaInput.value = data.dimensions.area || '';
          if (this.elements.perimeterInput) this.elements.perimeterInput.value = data.dimensions.perimeter || '';

          // Restore input method
          if (data.dimensions.inputMethod) {
            this.switchInputMethod(data.dimensions.inputMethod);
          }
        }

        // Restore selections
        if (data.selections) {
          this.state.selections = new Map(data.selections);

          // Check corresponding radio buttons and checkboxes
          this.state.selections.forEach((product, selectionKey) => {
            // Try radio button first (standard product steps)
            const radio = this.container.querySelector(`input[name="step-${selectionKey}-product"][value="${product.variantId}"]`);
            if (radio) {
              radio.checked = true;
              return;
            }

            // Try meststof checkbox
            if (String(selectionKey).startsWith('meststof-')) {
              const checkbox = this.container.querySelector(`input[name="${selectionKey}"][value="${product.variantId}"]`);
              if (checkbox) checkbox.checked = true;
            }

          });
        }

        // Restore calculation types
        if (data.calculationTypes) {
          this.state.calculationTypes = new Map(data.calculationTypes);
        }

        // Update UI
        this.updateCalculations();
        this.updateSidebarSelections();
      }
    } catch (e) {
      console.warn('Could not load from localStorage', e);
    }
  }

  /**
   * Clear storage
   */
  clearStorage() {
    try {
      localStorage.removeItem('groendak-configurator');
    } catch (e) {
      console.warn('Could not clear localStorage', e);
    }
  }

  /**
   * Update UI
   */
  updateUI() {
    this.updateCalculations();
  }
}

/**
 * Initialize configurator when DOM is ready
 */
document.addEventListener('DOMContentLoaded', () => {
  const container = document.querySelector('.groendak-configurator');

  if (container) {
    try {
      window.groendakConfiguratorInstance = new GroendakConfigurator(container);
      console.log('✅ Groendak Configurator geïnitialiseerd');
    } catch (error) {
      console.error('❌ Groendak Configurator initialisatie fout:', error);
    }
  } else {
    console.warn('⚠️ Groendak configurator container niet gevonden op deze pagina');
  }
});

/**
 * Expose for potential customization
 */
window.GroendakConfigurator = GroendakConfigurator;
